import { forwardRef, FunctionComponent, InputHTMLAttributes, SVGProps, useId } from "react";
import type { FieldError } from "react-hook-form";
import { cn } from "@/utils/helpers";
import { KitFieldMessage, kitFieldInvalidClass, kitFieldSurfaceClass, kitLabelClass } from "./Field";

export interface KitInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: FieldError | string;
  /** A faint line under the field. Hidden while an error shows. */
  hint?: string;
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  iconRight?: FunctionComponent<SVGProps<SVGSVGElement>>;
  onIconRightClick?: () => void;
  wrapperClassName?: string;
  inputClassName?: string;
  /** Rendered before the text input, inside the same field (e.g. a country-code Select). */
  leadingSlot?: React.ReactNode;
}

/*
 * Input (CreativeOS Library.lib.pen, Components / Inputs and selection, style
 * B): the label above, a 44px field in control glass at radius 12 with no line
 * at rest, a 2px accent ring outside it on focus, and an error as the same ring
 * in danger with the reason under it.
 */
export const KitInput = forwardRef<HTMLInputElement, KitInputProps>(
  (
    {
      label,
      error,
      hint,
      icon: Icon,
      iconRight: IconRight,
      onIconRightClick,
      wrapperClassName,
      inputClassName,
      leadingSlot,
      className,
      id,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const hasError = Boolean(error);
    const message = typeof error === "string" ? error : error?.message;

    return (
      <div className={cn("flex flex-col gap-2", wrapperClassName)}>
        {label && (
          <label htmlFor={inputId} className={kitLabelClass}>
            {label}
          </label>
        )}
        <div
          data-invalid={hasError || undefined}
          className={cn(
            "co-text-field group flex h-11 items-center gap-2.5 px-3.5",
            kitFieldSurfaceClass,
            hasError && kitFieldInvalidClass,
            className,
          )}
        >
          {leadingSlot}
          {Icon && (
            <Icon
              aria-hidden
              className="size-4 shrink-0 text-co-muted transition-colors group-focus-within:text-co-text"
            />
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={hasError || undefined}
            {...props}
            className={cn(
              "co-input-own-focus caret-co-accent h-full min-w-0 flex-1 bg-transparent text-[14px] text-co-text placeholder:text-co-faint outline-none focus-visible:!shadow-none",
              inputClassName,
            )}
          />
          {IconRight && (
            <button
              type="button"
              onClick={onIconRightClick}
              tabIndex={-1}
              aria-label="Toggle"
              className="grid size-8 -me-1.5 shrink-0 place-items-center rounded-full text-co-faint transition-colors hover:bg-co-hover hover:text-co-text"
            >
              <IconRight aria-hidden className="size-4" />
            </button>
          )}
        </div>
        {hasError && message ? (
          <KitFieldMessage>{message}</KitFieldMessage>
        ) : hint ? (
          <span className="text-[12.5px] leading-[1.45] text-co-faint">{hint}</span>
        ) : null}
      </div>
    );
  },
);

KitInput.displayName = "KitInput";
